/**
 * SOV (Share of Voice) Service
 *
 * API client for the SOV monitoring system.
 * Endpoints: /api/v1/synthesis/sov-*
 */

import { authFetch } from '@/utils/auth-fetch';

// ============================================================================
// Types
// ============================================================================

export interface SOVBasketQuery {
  id: string;
  site_id: string;
  query_text: string;
  query_type: string;
  tier: string;
  cluster_id: string | null;
  cluster_label: string | null;
  competitor_domain: string | null;
  priority_score: number;
  commercial_value: number;
  coverage_pct: number;
  funnel_stage: string | null;
  demand_velocity_label: string | null;
  check_frequency: string;
  providers: string[];
  enabled: boolean;
  aeo_deployed: boolean;
  aeo_proximity: number;
  nearest_product_title: string | null;
  last_checked_at: string | null;
  next_check_at: string | null;
  check_count: number;
}

export interface SOVBasketStats {
  selected: number;
  target: number;
  fill_rate: string;
  tier_distribution: Record<string, number>;
  funnel_distribution: Record<string, number>;
  cadence_distribution: Record<string, number>;
  coverage_distribution?: Record<string, number>;
  coverage_summary?: CoverageSummary;
  total_commercial_value_eur: number;
  estimated_monthly_cost_usd: number;
}

export interface SOVGroupData {
  checks: number;
  mentions: number;
  mention_rate: number;
  weighted_sov: number;
}

export interface CoverageBandData {
  checks: number;
  mentions: number;
  mention_rate: number;
  weighted_sov: number;
  avg_proximity: number;
  total_value_eur: number;
}

export interface CoverageSummary {
  total_clusters: number;
  total_market_value_eur: number;
  covered_value_eur: number;
  coverage_gap_eur: number;
  avg_proximity: number;
  covered_clusters: number;
}

export interface CoverageSnapshot {
  snapshot_at: string;
  total_clusters: number;
  total_market_value_eur: number;
  covered_clusters: number;
  covered_value_eur: number;
  avg_proximity: number;
  median_proximity: number;
  p90_proximity: number;
  proximity_histogram: Record<string, number>;
}

export interface SOVMetrics {
  aeo_sov_pct: number;
  total_checks: number;
  total_mentions: number;
  raw_mention_rate: number;
  sov_by_provider: Record<string, number> | null;
  sov_by_funnel: Record<string, number> | null;
  sov_by_group: Record<string, SOVGroupData> | null;
  sov_by_coverage: Record<string, CoverageBandData> | null;
  coverage_summary: CoverageSummary | null;
}

export interface KnowledgeLoopData {
  selfheal_queries_monitored: number;
  now_mentioned_in_ai: number;
  conversion_pct: number;
}

export interface ClusterAuthorityRow {
  cluster_id: string;
  cluster_label: string;
  total_commercial_value_eur: number;
  checks: number;
  mentions: number;
  mention_rate_pct: number;
  avg_citation_quality: number | null;
}

export interface CompetitorDisplacementRow {
  competitor_domain: string;
  total_checks: number;
  flipped_to_us: number;
  both_mentioned: number;
  competitor_holds: number;
  flip_rate_pct: number;
}

export interface SOVIndicators {
  knowledgeLoop: KnowledgeLoopData;
  clusterAuthority: ClusterAuthorityRow[];
  competitorDisplacement: CompetitorDisplacementRow[];
}

export interface SOVDashboardData {
  metrics: SOVMetrics;
  indicators: SOVIndicators;
}

export interface SOVCheckResult {
  id: string;
  checked_at: string;
  llm_provider: string;
  llm_model: string;
  query_text: string;
  query_type: string;
  query_strength: number;
  site_mentioned: boolean;
  product_mentioned: boolean;
  mention_position: number | null;
  mention_context: string | null;
  citation_quality: number | null;
  competitors_mentioned: string[];
  our_position_vs_competitors: number | null;
  funnel_stage: string | null;
  tier: string | null;
  aeo_deployed: boolean | null;
  aeo_proximity: number | null;
  nearest_product_title: string | null;
  cluster_label: string | null;
  commercial_value: number | null;
}

export interface TopCompetitor {
  competitor: string;
  times_mentioned: number;
  across_queries: number;
  by_providers: string[];
}

export interface CompetitorByQuery {
  query_text: string;
  llm_provider: string;
  competitors_mentioned: string[];
  site_mentioned: boolean;
  our_position_vs_competitors: number | null;
  aeo_deployed: boolean | null;
  aeo_proximity: number | null;
  funnel_stage: string | null;
}

export interface SOVCompetitorData {
  topCompetitors: TopCompetitor[];
  competitorByQuery: CompetitorByQuery[];
}

// ============================================================================
// Service
// ============================================================================

class SOVService {

  async getMetrics(siteId: string, days = 30): Promise<SOVDashboardData> {
    const response = await authFetch(`/api/v1/synthesis/sov-metrics/${siteId}?days=${days}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch SOV metrics (${response.status})`);
    }
    return response.json();
  }

  async getBasket(siteId: string, options: {
    tier?: string;
    funnel_stage?: string;
    cadence?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ total: number; queries: SOVBasketQuery[] }> {
    const params = new URLSearchParams();
    if (options.tier) params.append('tier', options.tier);
    if (options.funnel_stage) params.append('funnel_stage', options.funnel_stage);
    if (options.cadence) params.append('cadence', options.cadence);
    if (options.limit) params.append('limit', String(options.limit));
    if (options.offset) params.append('offset', String(options.offset));

    const qs = params.toString();
    const response = await authFetch(`/api/v1/synthesis/sov-basket/${siteId}${qs ? `?${qs}` : ''}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch SOV basket (${response.status})`);
    }
    return response.json();
  }

  async buildBasket(siteId: string, options: { basketSize?: number; dryRun?: boolean } = {}): Promise<{ success: boolean; stats?: SOVBasketStats; message?: string }> {
    const response = await authFetch(`/api/v1/synthesis/sov-basket/build/${siteId}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to build SOV basket (${response.status})`);
    }
    return response.json();
  }

  async runChecks(siteId: string, options: { maxChecks?: number; cadence?: string; force?: boolean } = {}): Promise<{ success: boolean; message: string }> {
    const response = await authFetch(`/api/v1/synthesis/sov-check/${siteId}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to start SOV checks (${response.status})`);
    }
    return response.json();
  }

  async getResults(siteId: string, days = 30, limit = 100): Promise<{ total: number; results: SOVCheckResult[] }> {
    const response = await authFetch(`/api/v1/synthesis/sov-results/${siteId}?days=${days}&limit=${limit}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch SOV results (${response.status})`);
    }
    return response.json();
  }

  async getCoverage(siteId: string, days = 90): Promise<{ snapshots: CoverageSnapshot[]; progression: any }> {
    const response = await authFetch(`/api/v1/synthesis/sov-coverage/${siteId}?days=${days}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch coverage data (${response.status})`);
    }
    return response.json();
  }

  async getCompetitors(siteId: string, days = 30): Promise<SOVCompetitorData> {
    const response = await authFetch(`/api/v1/synthesis/sov-competitors/${siteId}?days=${days}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch competitor data (${response.status})`);
    }
    return response.json();
  }
}

export const sovService = new SOVService();
